import { useGalleryQuery } from "@/lib/queries";
import { PlayCircle, ArrowLeftRight, Camera } from "lucide-react";
import { motion } from "framer-motion";
import { useSettings } from "@/context/SettingsContext";

const ICONS = {
  video: PlayCircle,
  photo: Camera,
  before_after: ArrowLeftRight,
};

const LABELS = {
  video: "Customer Video",
  photo: "Customer Photo",
  before_after: "Before & After",
};

export default function Gallery() {
  const { data: items = [] } = useGalleryQuery();
  const { settings } = useSettings();

  return (
    <section id="customers" className="relative py-24 md:py-32 bg-[#0A0A0A]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="text-center mb-14">
          <div className="text-[0.7rem] tracking-[0.32em] uppercase text-[#D4AF37] font-semibold mb-4" data-testid="gallery-eyebrow">
            {settings?.gallery_eyebrow}
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white max-w-3xl mx-auto" data-testid="gallery-headline">
            {settings?.gallery_headline}
          </h2>
          <p className="text-white/65 mt-5 max-w-2xl mx-auto" data-testid="gallery-subheadline">
            {settings?.gallery_subheadline}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[180px] md:auto-rows-[240px] gap-3 md:gap-4" data-testid="gallery-grid">
          {items.map((g, i) => {
            const Icon = ICONS[g.media_type] || Camera;
            const spans = [
              "col-span-2 row-span-2",
              "",
              "",
              "row-span-2",
              "",
              "col-span-2",
              "",
              "",
            ];
            const span = spans[i % spans.length];
            return (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, scale: 0.98 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`relative placeholder-frame ${span}`}
                data-testid={`gallery-item-${g.id}`}
              >
                <span className="placeholder-tag">{LABELS[g.media_type] || "Real Customer"}</span>
                {g.media_type === "video" && g.video ? (
                  <video
                    src={g.video}
                    poster={g.image || undefined}
                    controls
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover bg-black"
                    data-testid={`gallery-video-${g.id}`}
                  />
                ) : g.image ? (
                  <img src={g.image} alt={g.caption || "Real customer"} className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="placeholder-center">
                    <Icon className="w-8 h-8 text-[#D4AF37]/80" />
                    <div className="font-serif text-white text-base mt-2">{g.label}</div>
                    {g.caption && <div className="text-[0.65rem] uppercase tracking-[0.18em] text-white/45 mt-1">{g.caption}</div>}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
